'use client';

import { useEffect, useState, useRef } from 'react';
import { organizerInboxApi } from '@/lib/api';
import { usePathname } from 'next/navigation';
import { toast } from 'react-hot-toast';
import { useAuthStore } from '@/lib/auth-store';

const POLL_MS = 10000;

export function useOrganizerInboxUnread(eventId: string | null | undefined): number {
  const [count, setCount] = useState(0);
  const previousCountRef = useRef(0);
  const isInitialLoadRef = useRef(true);
  const pathname = usePathname();
  const pathnameRef = useRef(pathname);

  const authChecking = useAuthStore((state) => state.authChecking);
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);

  useEffect(() => {
    pathnameRef.current = pathname;
  }, [pathname]);

  useEffect(() => {
    if (!eventId || authChecking || !isAuthenticated) {
      setCount(0);
      previousCountRef.current = 0;
      isInitialLoadRef.current = true;
      return;
    }

    let cancelled = false;
    let interval: NodeJS.Timeout | null = null;

    const stopPolling = () => {
      if (interval) {
        clearInterval(interval);
        interval = null;
      }
    };

    const load = async () => {
      if (!useAuthStore.getState().isAuthenticated) {
        stopPolling();
        return;
      }
      try {
        const res = await organizerInboxApi.unread(eventId);
        if (!cancelled) {
          const newCount = res.data?.data?.unread_count ?? 0;
          
          if (!isInitialLoadRef.current && newCount > previousCountRef.current) {
            const isChatPage = pathnameRef.current?.includes('/communications') || 
                               pathnameRef.current?.includes('/chat') || 
                               pathnameRef.current?.includes('/inbox');
            if (!isChatPage) {
              toast.success('New message received in Support Chat', { icon: '💬' });
            }
          }
          
          isInitialLoadRef.current = false;
          previousCountRef.current = newCount;
          setCount(newCount);
        }
      } catch (err: any) {
        if (err?.response?.status === 401) {
          stopPolling();
        }
      }
    };

    const handleSessionExpired = () => {
      stopPolling();
      if (!cancelled) setCount(0);
    };

    window.addEventListener('auth:session-expired', handleSessionExpired);
    window.addEventListener('auth:unauthenticated', handleSessionExpired);

    void load();
    interval = setInterval(() => void load(), POLL_MS);
    return () => {
      cancelled = true;
      stopPolling();
      window.removeEventListener('auth:session-expired', handleSessionExpired);
      window.removeEventListener('auth:unauthenticated', handleSessionExpired);
    };
  }, [eventId, authChecking, isAuthenticated]);

  return count;
}
